import { Icon as MaterialIcon, MenuItem, Tooltip } from "@material-ui/core";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";

import { ActionPermissionsObject, IThemeStorageModel, IWorkspaceResourceTheme, PermissionActionType, WorkspacePlanTier, WorkspaceResourceType } from "common/interfaces";
import Banner from "js/Components/Banner";
import { Button as AppButton } from "js/Components/Button";
import { Icon as BaiIcon } from "js/Components/Icon";
import UpgradeIcon from "js/Components/UpgradeIcon";
import ThemesPaneController, { ThemesPaneControllerState } from "js/controllers/ThemesPaneController";
import WorkspaceController, { getPresentationsUsingSharedThemeCount, WorkspaceControllerState } from "js/controllers/WorkspaceController";
import AppController from "js/core/AppController";
import getLogger, { LogGroup } from "js/core/logger";
import themeManager, { SHARED_THEME_SORT } from "js/core/themeManager";
import { pluralize } from "js/core/utilities/utilities";
import withProgressDialog from "js/core/utilities/withProgressDialog";
import { ThemeThumbnail } from "js/editor/ThemeEditor/components/ThemeThumbnail";
import { TruncatedTooltip } from "js/editor/PresentationLibrary/components/TruncatedTooltip";
import { t } from "js/i18n";
import { ShowConfirmationDialog, ShowDialog, ShowErrorDialog } from "js/react/components/Dialogs/BaseDialog";
import ChangePlanDialog from "js/react/components/Dialogs/ChangePlanDialog";
import InputConfirmationDialog from "js/react/components/Dialogs/InputConfirmationDialog";
import FetchingClickShield from "js/react/components/FetchingClickShield";
import { Gap5 } from "js/react/components/Gap";
import { FlexBox } from "js/react/components/LayoutGrid";
import { NoMyResourcesThemeNotice } from "js/react/components/Notice";
import { useIntersectionObserver } from "js/react/hooks/useIntersectionObserver";
import { MultiSelectThumbnailGrid, ThumbnailGridSkeleton } from "js/react/views/AddSlide/Panes/Components/ThumbnailGrid";
import { Header, Layout, PaneBody, PaneContent, teamResourceGridProps } from "js/react/views/SharedResources/Components";
import {
    TeamResourceBadgeCluster,
    TeamResourceIconMenu,
    TeamResourceThumbnailActions,
    TeamResourceThumbnailCard,
    TeamResourceThumbnailImageWrap,
    TeamResourceUsageBadge,
} from "js/react/views/SharedResources/Components/TeamResourceThumbnailCard";

const logger = getLogger(LogGroup.TEAMS);

const ThemeThumbnailContent = styled.div`
    display: flex;
    flex-direction: column;
    gap: 2px;
    width: 100%;
    min-width: 0;
    padding: 0;
    margin-top: 4px;
`;

const ThemeTitleRow = styled.div`
    display: flex;
    align-items: center;
    gap: 8px;
    width: 100%;
    min-width: 0;
    min-height: 20px;
`;

const ThemeTitleText = styled.div`
    font-size: 14px;
    line-height: 18px;
    letter-spacing: 0px;
    color: #444;
    font-weight: 600;
    white-space: nowrap;
    text-overflow: ellipsis;
    overflow: hidden;
    text-align: left;
    flex: 1;
    min-width: 0;
    text-transform: none;
    margin: 0;
`;

const ThemePreviewWrap = styled(TeamResourceThumbnailImageWrap)`
    .thumbnail-container {
        gap: 0;
    }

    .thumbnail {
        border: none !important;
        box-shadow: none !important;
    }
`;

const StyledAppButton = styled(AppButton)`
    letter-spacing: 0.5px;
`;

const ResultsContainer = styled.div`
    flex: 1;
    width: 100%;
    min-width: 0;
    min-height: 0;
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
`;

const DefaultBadge = styled.div`
    background: #E1EDF3;
    border-radius: 999px;
    flex: 0 0 auto;
    height: 20px;
    padding: 0 8px;
    color: #11A9E2;
    font-size: 11px;
    font-weight: 600;
    line-height: 20px;
    text-transform: uppercase;
    white-space: nowrap;
`;

const DraggableThemeThumbnail = styled.div<{ $canReorder: boolean }>`
    min-height: 220px;
    cursor: ${props => props.$canReorder ? "grab" : "default"};
`;

const DropPlaceholder = styled.div`
    min-height: 220px;
    height: 100%;
    border: 1px dashed #A9B5BD;
    border-radius: 4px;
    background: #F1F5F7;
`;

const DragPreview = styled.div`
    position: fixed;
    z-index: 2000;
    pointer-events: none;
    opacity: 0.95;
    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.25);
    cursor: grabbing;
`;

// Matches the slide grid: the thumbnail only lifts once the pointer has clearly moved,
// so a click to open the menu or a double click to edit still lands.
const DRAG_START_DISTANCE = 6;
const DRAG_AUTOSCROLL_MARGIN = 60;
const DRAG_AUTOSCROLL_SPEED = 12;

type ThemeRecord = ThemesPaneControllerState["data"][number];

type DragState = {
    resourceId: string;
    startX: number;
    startY: number;
    offsetX: number;
    offsetY: number;
    width: number;
    height: number;
    fromIndex: number;
    cellCenters: { x: number, y: number }[];
    started: boolean;
    pointerX: number;
    pointerY: number;
    autoScrollFrame: number;
};

/**
 * Marks a grid cell so a drag can measure the layout. It sits on the cell rather than on the
 * card inside it, which isn't rendered until the kit's theme has loaded.
 */
const THEME_CELL_ATTRIBUTE = "data-theme-cell-id";

function moveItem<T>(items: T[], fromIndex: number, toIndex: number) {
    const reordered = [...items];
    const [item] = reordered.splice(fromIndex, 1);
    reordered.splice(toIndex, 0, item);
    return reordered;
}

function SharedThemeThumbnail(props: {
    resource: IWorkspaceResourceTheme,
    isDefault: boolean,
    isOnlyVisibleTheme: boolean,
    canReorder?: boolean,
    isDragPreview?: boolean,
    onDragStart?: (resource: IWorkspaceResourceTheme, event: React.MouseEvent) => void,
    onVisibleChange?: (visible: boolean, resource: IWorkspaceResourceTheme) => void
}) {
    const { resource, isDefault, isOnlyVisibleTheme, canReorder = false, isDragPreview = false, onDragStart, onVisibleChange } = props;

    const containerRef = useRef<HTMLDivElement>(null);
    const [theme, setTheme] = useState<IThemeStorageModel>(null);
    const [isHovering, setIsHovering] = useState(false);
    const [usageCount, setUsageCount] = useState(0);
    const observedVisible = useIntersectionObserver(containerRef);
    // The floating drag preview is always on screen and must not re-run the pane's
    // load-more or usage-count work while the kit is being dragged.
    const visible = isDragPreview || observedVisible;

    useEffect(() => {
        if (!visible) {
            return;
        }

        themeManager.getSharedTheme(resource.resourceId, true)
            .then(setTheme)
            .catch(err => {
                logger.error(err, "[TeamSharedThemesPane] Error getting shared theme");
            });

        return () => {
            setTheme(null);
        };
    }, [resource.resourceId, visible]);

    useEffect(() => {
        onVisibleChange?.(observedVisible, resource);
    }, [observedVisible]);

    useEffect(() => {
        let isMounted = true;

        if (!visible || isDragPreview) {
            return () => {
                isMounted = false;
            };
        }

        getPresentationsUsingSharedThemeCount({ workspaceId: WorkspaceController.workspaceId, themeId: resource.resourceId })
            .then(count => {
                if (isMounted) {
                    setUsageCount(count);
                }
            })
            .catch(err => {
                logger.error(err, "[TeamSharedThemesPane] Error getting shared theme usage count", { themeId: resource.resourceId });
                if (isMounted) {
                    setUsageCount(0);
                }
            });

        return () => {
            isMounted = false;
        };
    }, [resource.resourceId, visible]);

    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);

    const handleEdit = async () => {
        const teamThemeUsageCount = await getPresentationsUsingSharedThemeCount({ workspaceId: WorkspaceController.workspaceId, themeId: resource.resourceId });

        if (teamThemeUsageCount > 0) {
            const shouldContinue = await ShowConfirmationDialog({
                title: t("Editing Shared Brand Kit"),
                message: `Any changes you make will impact ${pluralize(
                    "presentation",
                    teamThemeUsageCount,
                    "presentations"
                )} in your workspace.`,
                cancelButtonLabel: "Cancel",
                okButtonLabel: "Continue editing",
                optOutProperty: "editTeamThemeDialog"
            });

            if (!shouldContinue) {
                return;
            }
        }

        AppController.showThemeEditor({ themeId: resource.resourceId });
    };

    const handleRename = () => {
        ShowDialog(InputConfirmationDialog, {
            title: t("Rename Theme"),
            message: null,
            input: {
                value: resource.metadata.name
            },
            acceptCallback: async (name: string) => {
                try {
                    await withProgressDialog({
                        title: t("Renaming theme..."),
                        action: () => themeManager.updateThemeName(resource.resourceId, name.trim())
                    });
                } catch (err) {
                    logger.error(err, "[TeamSharedThemesPane] Error renaming theme");

                    ShowErrorDialog({
                        title: t("Unable to rename theme"),
                        message: err.message
                    });
                }
            },
        });
    };

    const handleDuplicate = async () => {
        try {
            const theme = await themeManager.getSharedTheme(resource.resourceId, true);
            await withProgressDialog({
                title: t("Duplicating theme..."),
                action: async () => {
                    const name = t("Copy of {{name}}", { name: resource.metadata.name });
                    await themeManager.createTeamTheme(theme.attributes, name);
                }
            });
        } catch (err) {
            logger.error(err, "[TeamSharedThemesPane] Error duplicating theme");

            ShowErrorDialog({
                title: t("Unable to duplicate theme"),
                message: err.message
            });
        }
    };

    const handleDelete = async () => {
        const teamThemeUsageCount = await getPresentationsUsingSharedThemeCount({ workspaceId: WorkspaceController.workspaceId, themeId: resource.resourceId });

        if (teamThemeUsageCount > 0) {
            const confirmed = await ShowConfirmationDialog({
                title: t("Are you sure you want to delete this Team Theme?"),
                message: t("Deleting this Team Theme permanently removes it from your Team Resources. Presentations currently using this theme will keep their existing design, but editors will be able to modify those themes without restriction."),
                okButtonLabel: t("Delete Team Theme"),
                buttonOptions: { acceptButtonColor: "red" }
            });

            if (!confirmed) {
                return;
            }
        }

        try {
            await withProgressDialog({
                title: t("Deleting theme..."),
                action: () => themeManager.deleteTheme(resource.resourceId)
            });
        } catch (err) {
            logger.error(err, "[TeamSharedThemesPane] Error deleting theme");
            ShowErrorDialog({
                title: t("Unable to delete theme"),
                message: err.message
            });
        }
    };

    const handleToggleHide = async () => {
        try {
            await withProgressDialog({
                title: resource.metadata.isPublished ? t("Hiding theme...") : t("Unhiding theme..."),
                action: () => themeManager.updateThemeIsPublished(resource.resourceId, !resource.metadata.isPublished)
            });
        } catch (err) {
            logger.error(err, "[TeamSharedThemesPane] Error toggling hide theme");

            ShowErrorDialog({
                title: t("Unable to toggle hide theme"),
                message: err.message
            });
        }
    };

    // A workspace must always keep one visible brand kit, so its last visible one can be
    // neither hidden nor deleted - every other kit, including the first one ever created, can.
    const canHide = resource.metadata.isPublished && !isOnlyVisibleTheme;
    const showHideMenu = canHide || !resource.metadata.isPublished;
    const canDelete = !isOnlyVisibleTheme;
    const hoverActions = isHovering && !isDragPreview && (
        <TeamResourceThumbnailActions
            onMouseDown={event => {
                event.preventDefault();
                event.stopPropagation();
            }}
            onMouseUp={event => {
                event.preventDefault();
                event.stopPropagation();
            }}
            onDoubleClick={event => {
                event.preventDefault();
                event.stopPropagation();
            }}
            onClick={event => event.stopPropagation()}
        >
            <TeamResourceIconMenu handleCloseMenu={() => setIsHovering(false)} fontSize="medium">
                {canUpgradeToManageSharedResources ? [
                    <MenuItem key="edit" disabled divider><UpgradeIcon /><Gap5 />{t("Edit Theme...")}</MenuItem>,
                    <MenuItem key="rename" disabled divider><UpgradeIcon /><Gap5 />{t("Rename")}</MenuItem>,
                    <MenuItem key="duplicate" disabled divider><UpgradeIcon /><Gap5 />{t("Duplicate")}</MenuItem>,
                    <hr key="divider" />,
                    ...(showHideMenu ? [
                        resource.metadata.isPublished
                            ? <MenuItem key="hide" disabled divider><UpgradeIcon /><Gap5 />{t("Hide")}</MenuItem>
                            : <MenuItem key="unhide" disabled divider><UpgradeIcon /><Gap5 />{t("Show")}</MenuItem>
                    ] : [])
                ].filter(Boolean) : [
                    <MenuItem key="edit" onClick={() => handleEdit()}>
                        <MaterialIcon>edit</MaterialIcon>{t("Edit Theme...")}
                    </MenuItem>,
                    <MenuItem key="rename" onClick={() => handleRename()}>
                        <MaterialIcon>label</MaterialIcon>{t("Rename")}
                    </MenuItem>,
                    <MenuItem key="duplicate" onClick={() => handleDuplicate()}>
                        <MaterialIcon>content_copy</MaterialIcon>{t("Duplicate")}
                    </MenuItem>,
                    <hr key="divider" />,
                    ...(showHideMenu ? [
                        resource.metadata.isPublished ? (
                            <MenuItem key="hide" onClick={() => handleToggleHide()}>
                                <MaterialIcon>visibility_off</MaterialIcon>{t("Hide")}
                            </MenuItem>
                        ) : (
                            <MenuItem key="unhide" onClick={() => handleToggleHide()}>
                                <MaterialIcon>visibility_on</MaterialIcon>{t("Show")}
                            </MenuItem>
                        )
                    ] : [])
                ].filter(Boolean)}
                {canDelete && (
                    canUpgradeToManageSharedResources ? (
                        <MenuItem key="delete" disabled divider><UpgradeIcon /><Gap5 />{t("Delete...")}</MenuItem>
                    ) : (
                        <MenuItem key="delete" onClick={() => handleDelete()}>
                            <MaterialIcon>delete</MaterialIcon>{t("Delete...")}
                        </MenuItem>
                    )
                )}
            </TeamResourceIconMenu>
        </TeamResourceThumbnailActions>
    );

    return (
        <DraggableThemeThumbnail
            ref={containerRef}
            {...(isDragPreview ? {} : { [THEME_CELL_ATTRIBUTE]: resource.id })}
            $canReorder={canReorder}
            onMouseDown={canReorder ? event => onDragStart?.(resource, event) : undefined}
        >
            {!theme && <ThumbnailGridSkeleton />}
            {theme && (
                <TeamResourceThumbnailCard
                    data-item-id={resource.id}
                    selected={false}
                    onMouseEnter={() => setIsHovering(true)}
                    onMouseLeave={() => setIsHovering(false)}
                    barVariant="custom"
                    barContent={(
                        <ThemeThumbnailContent>
                            <ThemeTitleRow>
                                <TruncatedTooltip title={resource.metadata.name} enterDelay={500} enterNextDelay={500}>
                                    <ThemeTitleText>{resource.metadata.name}</ThemeTitleText>
                                </TruncatedTooltip>
                                <TeamResourceBadgeCluster>
                                    {isDefault && (
                                        <Tooltip
                                            title={t("Used by default when generating presentations and previewing other team resources. Drag another Brand Kit to the front to change it.")}
                                            placement="top"
                                            enterDelay={500}
                                            enterNextDelay={500}
                                            arrow
                                        >
                                            <DefaultBadge>{t("Default")}</DefaultBadge>
                                        </Tooltip>
                                    )}
                                    <TeamResourceUsageBadge
                                        count={usageCount}
                                        showTitle
                                        title={t("Team Theme")}
                                        description={t("Only Librarians and Owners can edit. Changes apply everywhere this is used.")}
                                    />
                                </TeamResourceBadgeCluster>
                            </ThemeTitleRow>
                        </ThemeThumbnailContent>
                    )}
                >
                    <ThemePreviewWrap>
                        <ThemeThumbnail
                            item={theme}
                            resource={resource}
                            isDefault={isDefault}
                            isDowngraded={canUpgradeToManageSharedResources}
                            hideBar
                            themeThumbnailGrid
                            onDoubleClick={canUpgradeToManageSharedResources ? undefined : (() => handleEdit())}
                        />
                        {hoverActions}
                    </ThemePreviewWrap>
                </TeamResourceThumbnailCard>
            )}
        </DraggableThemeThumbnail>
    );
}

function TeamSharedThemesPane(props: WorkspaceControllerState & ThemesPaneControllerState) {
    const { fetching, data } = props;

    const resultsRef = useRef<HTMLDivElement>(null);
    const dragRef = useRef<DragState>(null);
    const commitOrderRef = useRef<(fromIndex: number, toIndex: number) => void>(null);
    // Ordering the user just dropped, applied until the controller's data catches up so the
    // grid never snaps back to the old order while the request is in flight.
    const [orderOverride, setOrderOverride] = useState<string[]>(null);
    const [drag, setDrag] = useState<{ resourceId: string, dropIndex: number, left: number, top: number, width: number, height: number }>(null);

    const canManageSharedResources = WorkspaceController.actionPermissions[ActionPermissionsObject.SHARED_RESOURCES].manage;
    const canUpgradeToManageSharedResources = WorkspaceController.canUpgradeToRemoveActionRestriction(ActionPermissionsObject.SHARED_RESOURCES, PermissionActionType.MANAGE);
    const canReorder = canManageSharedResources && !canUpgradeToManageSharedResources;

    const orderedData = useMemo(() => {
        if (!orderOverride) {
            return data;
        }

        const recordsById = new Map(data.map(record => [record.resource.id, record]));
        const overridden = orderOverride.map(id => recordsById.get(id)).filter(Boolean);
        const overriddenIds = new Set(overridden.map(record => record.resource.id));

        return [...overridden, ...data.filter(record => !overriddenIds.has(record.resource.id))];
    }, [data, orderOverride]);

    useEffect(() => {
        if (!orderOverride) {
            return;
        }

        const loadedIds = data.map(record => record.resource.id);
        if (orderOverride.every((id, index) => loadedIds[index] === id)) {
            setOrderOverride(null);
        }
    }, [data, orderOverride]);

    // The dragged kit follows the pointer while the rest close up around the gap it left, so
    // the placeholder always sits where the kit will land.
    const displayedData = useMemo(() => {
        if (!drag) {
            return orderedData;
        }

        const fromIndex = orderedData.findIndex(record => record.resource.id === drag.resourceId);
        if (fromIndex === -1) {
            return orderedData;
        }

        return moveItem(orderedData, fromIndex, drag.dropIndex);
    }, [orderedData, drag]);

    const handleCreate = () => {
        AppController.showThemeEditor({ themeId: "new-team-theme" });
    };

    const handleThumbnailVisibilityChange = (visible: boolean, resource: IWorkspaceResourceTheme) => {
        if (!visible || drag) {
            return;
        }

        if (!ThemesPaneController.canLoadMore) {
            return;
        }

        const index = orderedData.findIndex(record => record.resource.id === resource.id);
        if (index === orderedData.length - 1) {
            ThemesPaneController.loadMore();
        }
    };

    const handleDragStart = useCallback((resource: IWorkspaceResourceTheme, event: React.MouseEvent) => {
        const container = resultsRef.current;
        if (event.button !== 0 || !container) {
            return;
        }

        const cell = event.currentTarget as HTMLElement;
        const cells = Array.from(container.querySelectorAll(`[${THEME_CELL_ATTRIBUTE}]`)) as HTMLElement[];
        const cellRect = cell.getBoundingClientRect();

        dragRef.current = {
            resourceId: resource.id,
            startX: event.clientX,
            startY: event.clientY,
            offsetX: event.clientX - cellRect.left,
            offsetY: event.clientY - cellRect.top,
            width: cellRect.width,
            height: cellRect.height,
            fromIndex: cells.indexOf(cell),
            // Captured once: the grid keeps the same number of cells throughout the drag
            // (the dragged kit leaves a placeholder behind), so these stay valid and the drop
            // target can't oscillate as the kits reflow underneath the pointer.
            cellCenters: cells.map(gridCell => {
                const rect = gridCell.getBoundingClientRect();
                return {
                    x: rect.left + rect.width / 2 + container.scrollLeft,
                    y: rect.top + rect.height / 2 + container.scrollTop
                };
            }),
            started: false,
            pointerX: event.clientX,
            pointerY: event.clientY,
            autoScrollFrame: null
        };
    }, []);

    useEffect(() => {
        const getDropIndex = (dragState: DragState) => {
            const container = resultsRef.current;
            if (!container) {
                return dragState.fromIndex;
            }

            const x = dragState.pointerX + container.scrollLeft;
            const y = dragState.pointerY + container.scrollTop;

            let dropIndex = dragState.fromIndex;
            let closestDistance = Infinity;
            dragState.cellCenters.forEach((center, index) => {
                const distance = Math.pow(center.x - x, 2) + Math.pow(center.y - y, 2);
                if (distance < closestDistance) {
                    closestDistance = distance;
                    dropIndex = index;
                }
            });

            return dropIndex;
        };

        const updateDrag = () => {
            const dragState = dragRef.current;
            if (!dragState?.started) {
                return;
            }

            setDrag({
                resourceId: dragState.resourceId,
                dropIndex: getDropIndex(dragState),
                left: dragState.pointerX - dragState.offsetX,
                top: dragState.pointerY - dragState.offsetY,
                width: dragState.width,
                height: dragState.height
            });
        };

        const autoScroll = () => {
            const dragState = dragRef.current;
            const container = resultsRef.current;
            if (!dragState?.started || !container) {
                return;
            }

            const bounds = container.getBoundingClientRect();
            if (dragState.pointerY < bounds.top + DRAG_AUTOSCROLL_MARGIN) {
                container.scrollTop -= DRAG_AUTOSCROLL_SPEED;
                updateDrag();
            } else if (dragState.pointerY > bounds.bottom - DRAG_AUTOSCROLL_MARGIN) {
                container.scrollTop += DRAG_AUTOSCROLL_SPEED;
                updateDrag();
            }

            dragState.autoScrollFrame = window.requestAnimationFrame(autoScroll);
        };

        const handleMouseMove = (event: MouseEvent) => {
            const dragState = dragRef.current;
            if (!dragState) {
                return;
            }

            dragState.pointerX = event.clientX;
            dragState.pointerY = event.clientY;

            if (!dragState.started) {
                if (Math.hypot(event.clientX - dragState.startX, event.clientY - dragState.startY) < DRAG_START_DISTANCE) {
                    return;
                }

                dragState.started = true;
                dragState.autoScrollFrame = window.requestAnimationFrame(autoScroll);
            }

            // Keeps the pointer from selecting the kit names it sweeps across.
            event.preventDefault();
            updateDrag();
        };

        const handleMouseUp = () => {
            const dragState = dragRef.current;
            dragRef.current = null;

            if (!dragState) {
                return;
            }

            if (dragState.autoScrollFrame !== null) {
                window.cancelAnimationFrame(dragState.autoScrollFrame);
            }

            if (!dragState.started) {
                return;
            }

            const dropIndex = getDropIndex(dragState);
            setDrag(null);

            if (dropIndex !== dragState.fromIndex) {
                commitOrderRef.current(dragState.fromIndex, dropIndex);
            }
        };

        window.addEventListener("mousemove", handleMouseMove);
        window.addEventListener("mouseup", handleMouseUp);

        return () => {
            window.removeEventListener("mousemove", handleMouseMove);
            window.removeEventListener("mouseup", handleMouseUp);
        };
    }, []);

    commitOrderRef.current = async (fromIndex: number, toIndex: number) => {
        const reordered = moveItem(orderedData, fromIndex, toIndex);
        setOrderOverride(reordered.map(record => record.resource.id));

        try {
            await themeManager.updateSharedThemesOrder(reordered.map(record => record.resource.resourceId));
        } catch (err) {
            logger.error(err, "[TeamSharedThemesPane] Error reordering shared themes");
            setOrderOverride(null);

            ShowErrorDialog({
                title: t("Unable to reorder Brand Kits"),
                message: err.message
            });
        }
    };

    const isLoadingMore = fetching && orderedData.length > 0;
    const isLoadingInitial = fetching && orderedData.length === 0;
    // Count published themes in the loaded page only, not the workspace total from the API.
    // Pagination may not have fetched every published theme yet; using the API count could
    // show "Hide" on the only published theme in view while others are still on later pages.
    const publishedRecords = displayedData.filter(record => record.resource.metadata.isPublished);
    // The team's default Brand Kit is simply the first visible one in the order they set.
    const defaultResourceId = publishedRecords[0]?.resource.id;
    const hasSingleVisibleTheme = publishedRecords.length === 1;
    const draggedRecord = drag && displayedData.find(record => record.resource.id === drag.resourceId);

    return (
        <Layout>
            {canManageSharedResources && (
                <Header>
                    <StyledAppButton onClick={handleCreate} blue large>
                        <BaiIcon white fill>add_circle</BaiIcon>
                        {t("New Theme")}
                    </StyledAppButton>
                </Header>
            )}
            {canUpgradeToManageSharedResources && (
                <FlexBox center fillWidth>
                    <Banner
                        headerText={t("Unlock Themes")}
                        bodyText={t("Upgrade your plan to keep colors, fonts, and logos consistent across presentations.")}
                        buttonLabel={t("Upgrade")}
                        onButtonClick={() => ShowDialog(ChangePlanDialog,
                            {
                                featuredPlan: WorkspacePlanTier.TEAM,
                                analytics: { cta: "team_themes" }
                            })
                        }
                    />
                </FlexBox>
            )}
            <PaneBody>
                <PaneContent>
                    <ResultsContainer ref={resultsRef}>
                        <FetchingClickShield visible={isLoadingInitial} backgroundColor="#EEEEEE" />
                        {displayedData.length === 0 && <NoMyResourcesThemeNotice />}
                        {displayedData.length > 0 && (
                            <MultiSelectThumbnailGrid
                                {...teamResourceGridProps}
                                items={displayedData}
                                allowMultiSelect={false}
                                allowDragging={false}
                                readOnly
                                isLoadingMore={isLoadingMore}
                                getItemKey={(record: ThemeRecord) => record.resource.id}
                                renderThumbnail={(record: ThemeRecord) => record.resource.id === drag?.resourceId
                                    ? <DropPlaceholder />
                                    : (
                                        <SharedThemeThumbnail
                                            resource={record.resource}
                                            isDefault={record.resource.id === defaultResourceId}
                                            isOnlyVisibleTheme={hasSingleVisibleTheme && record.resource.metadata.isPublished}
                                            canReorder={canReorder}
                                            onDragStart={handleDragStart}
                                            onVisibleChange={handleThumbnailVisibilityChange}
                                        />
                                    )}
                            />
                        )}
                        {draggedRecord && (
                            <DragPreview style={{ left: drag.left, top: drag.top, width: drag.width, height: drag.height }}>
                                <SharedThemeThumbnail
                                    resource={draggedRecord.resource}
                                    isDefault={draggedRecord.resource.id === defaultResourceId}
                                    isOnlyVisibleTheme={hasSingleVisibleTheme && draggedRecord.resource.metadata.isPublished}
                                    isDragPreview
                                />
                            </DragPreview>
                        )}
                    </ResultsContainer>
                </PaneContent>
            </PaneBody>
        </Layout>
    );
}

function TeamSharedThemesPaneWrapper(props: WorkspaceControllerState & ThemesPaneControllerState) {
    useEffect(() => {
        const filters: ThemesPaneControllerState["filters"] = [
            {
                field: "resource.metadata.isShared",
                value: true,
                operator: "eq"
            },
        ];

        ThemesPaneController.initialize({
            workspaceId: WorkspaceController.workspaceId,
            resourceTypes: [WorkspaceResourceType.THEME],
            sort: [...SHARED_THEME_SORT] as ThemesPaneControllerState["sort"],
            filters
        });

        return () => {
            ThemesPaneController.reset();
        };
    }, []);

    if (!ThemesPaneController.initialized) {
        return <Layout><FetchingClickShield visible backgroundColor="#eee" /></Layout>;
    }

    return <TeamSharedThemesPane {...props} />;
}

export default WorkspaceController.withInitializedState(ThemesPaneController.withState(TeamSharedThemesPaneWrapper));
